import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost, apiDelete } from '../config/apiClient';
import AddToBasketButton from '../components/AddToBasketButton';

const BasicPriceChart = ({ historyData }) => {
  if (!historyData || historyData.length === 0) return null;

  const chartHeight = 160;
  const barPadding = 5;
  const chartWidth = Math.max(300, historyData.length * 50);
  const numBars = historyData.length;
  const barWidth = Math.min(40, (chartWidth - (numBars + 1) * barPadding) / numBars);

  const prices = historyData.map(d => parseFloat(d.price));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice;

  const getBarHeight = (price) => {
    if (priceRange === 0) return chartHeight - 30;
    return ((parseFloat(price) - minPrice) / priceRange) * (chartHeight - 40) + 15;
  };

  return (
    <div className="mt-4 p-4 border rounded-lg bg-gray-50 overflow-x-auto">
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} width="100%" preserveAspectRatio="xMidYMid meet" className="mx-auto">
        <line x1="0" y1={chartHeight - 18} x2={chartWidth} y2={chartHeight - 18} stroke="#e2e8f0" strokeWidth="1" />
        {historyData.map((entry, index) => {
          const barH = getBarHeight(entry.price);
          const x = barPadding + index * (barWidth + barPadding);
          const y = chartHeight - 18 - barH;
          return (
            <g key={index}>
              <rect x={x} y={y} width={barWidth} height={barH} fill="#991b1b" rx="3" />
              <text x={x + barWidth / 2} y={chartHeight - 4} fontSize="9" fill="#9ca3af" textAnchor="middle">
                {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </text>
              <text x={x + barWidth / 2} y={y - 5} fontSize="9" fill="#374151" textAnchor="middle" fontWeight="600">
                ${parseFloat(entry.price).toFixed(2)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default function ProductDetailPage() {
  const { productIdentifier } = useParams();
  const [product, setProduct] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [interactionLoading, setInteractionLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const feedbackTimer = useRef(null);
  const { authToken, isAuthenticated } = useAuth();

  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback({ message: '', type: '' }), 3500);
  };

  useEffect(() => {
    return () => { if (feedbackTimer.current) clearTimeout(feedbackTimer.current); };
  }, []);

  useEffect(() => {
    if (!productIdentifier) return;

    const fetchProductDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiGet(`/products/${productIdentifier}`);
        setProduct(data);
        if (data.is_saved_by_user !== undefined) {
          setIsSaved(data.is_saved_by_user);
        }
      } catch (err) {
        console.error('Fetch product error:', err);
        setError(err.data?.message || err.message || 'Could not load product. Check your connection.');
      }
      setLoading(false);
    };

    fetchProductDetails();
  }, [productIdentifier, authToken]);

  const handleSaveToggle = async () => {
    if (!isAuthenticated || !product) return;

    setInteractionLoading(true);
    const endpoint = `/products/${product.id}/${isSaved ? 'unsave' : 'save'}`;

    try {
      if (isSaved) {
        await apiDelete(endpoint);
      } else {
        await apiPost(endpoint);
      }
      setIsSaved(!isSaved);
      showFeedback(!isSaved ? 'Now watching this product' : 'Removed from watchlist', 'success');
    } catch (err) {
      console.error('Save/unsave error:', err);
      showFeedback(err.data?.message || err.message || 'Network error. Try again.', 'error');
    }
    setInteractionLoading(false);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="animate-pulse">
          <div className="flex flex-col md:flex-row gap-8 bg-white p-6 rounded-lg shadow-lg">
            <div className="w-full md:w-1/3 h-64 bg-gray-200 rounded-lg"></div>
            <div className="md:w-2/3 space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              <div className="h-10 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1">Couldn't load product</h3>
        <p className="text-sm text-gray-500 mb-4">{error}</p>
        <Link to="/search" className="text-red-800 hover:underline text-sm">Back to search</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Product not found</h3>
        <Link to="/search" className="text-red-800 hover:underline text-sm">Back to search</Link>
      </div>
    );
  }

  const imageUrl = product.image_url || product.image || product.img || '/placeholder-product.svg';
  const categoryName = product.category?.name;
  const brandName = product.brand?.name;

  const storePrices = product.store_prices_data || [];
  const hasUnitPricing = storePrices.some(sp => sp.unit_price && sp.unit);
  const getDisplayPrice = (sp) => hasUnitPricing && sp.unit_price ? parseFloat(sp.unit_price) : parseFloat(sp.price);
  const cheapestStore = storePrices.length > 0
    ? storePrices.reduce((min, sp) => getDisplayPrice(sp) < getDisplayPrice(min) ? sp : min, storePrices[0])
    : null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 sm:py-10 pb-24 md:pb-10">
      {/* Product header */}
      <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start mb-8 bg-white p-5 sm:p-6 rounded-xl shadow-lg">
        <img 
          src={imageUrl} 
          alt={product.name} 
          className="w-full md:w-1/3 max-w-[280px] h-auto object-contain rounded-lg shadow-sm self-center md:self-start"
        />
        <div className="md:w-2/3 w-full">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">{product.name || 'Product'}</h1>
          
          <div className="flex flex-wrap gap-2 mb-3">
            {categoryName && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{categoryName}</span>
            )}
            {brandName && (
              <span className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full">{brandName}</span>
            )}
          </div>

          {(product.price || (cheapestStore && cheapestStore.unit_price)) && (
            <div className="mb-3">
              {hasUnitPricing && cheapestStore && cheapestStore.unit_price ? (
                <>
                  <div className="text-red-800 font-bold text-3xl">
                    ${parseFloat(cheapestStore.unit_price).toFixed(cheapestStore.unit_price % 1 !== 0 ? 3 : 2)}
                    <span className="text-lg font-semibold text-gray-500">/{cheapestStore.unit}</span>
                  </div>
                </>
              ) : (
                <div className="text-red-800 font-bold text-3xl">${parseFloat(product.price).toFixed(2)}</div>
              )}
              {cheapestStore && (
                <p className="text-sm text-gray-500 mt-0.5">
                  Best price at <strong className="text-gray-700">{cheapestStore.store_name}</strong>
                  {cheapestStore.last_updated && (
                    <span className="text-gray-400"> &middot; Updated {new Date(cheapestStore.last_updated).toLocaleDateString()}</span>
                  )}
                </p>
              )}
            </div>
          )}
          
          {product.description && (
            <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">{product.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {isAuthenticated && (
              <button 
                onClick={handleSaveToggle}
                disabled={interactionLoading}
                className={`font-semibold px-5 py-2.5 rounded-lg transition text-sm min-h-[44px]
                  ${isSaved 
                    ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100' 
                    : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'}
                  disabled:opacity-50`}
              >
                {interactionLoading ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                    Saving...
                  </span>
                ) : (isSaved ? '♥ Watching Price' : '♡ Watch Price')}
              </button>
            )}
            {!isAuthenticated && (
              <Link to="/login" className="text-sm text-red-800 hover:underline">Login to watch this price</Link>
            )}
            {product && (
              <div className="w-36">
                <AddToBasketButton productId={product.id} />
              </div>
            )}
          </div>

          {feedback.message && (
            <p className={`text-sm mt-2 transition-opacity duration-200 ${
              feedback.type === 'error' ? 'text-red-500' : 'text-green-600'
            }`}>
              {feedback.message}
            </p>
          )}
        </div>
      </div>

      {/* Store price comparison — shown first for quick decision-making */}
      {storePrices.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-5 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
            <svg className="w-5 h-5 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" /></svg>
            Compare Prices ({storePrices.length} store{storePrices.length !== 1 ? 's' : ''})
          </h2>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b-2 border-gray-100">
                  <th className="px-3 py-2.5 font-semibold text-gray-500 text-xs uppercase tracking-wider">Store</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-500 text-xs uppercase tracking-wider text-right">
                    {hasUnitPricing ? 'Unit Price' : 'Price'}
                  </th>
                  <th className="px-3 py-2.5 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden sm:table-cell">Updated</th>
                </tr>
              </thead>
              <tbody>
                {[...storePrices]
                  .sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b))
                  .map((storePrice, index) => {
                    const displayPrice = getDisplayPrice(storePrice);
                    const cheapestDisplayPrice = cheapestStore ? getDisplayPrice(cheapestStore) : null;
                    const isCheapest = cheapestDisplayPrice !== null && displayPrice === cheapestDisplayPrice;
                    const diff = cheapestDisplayPrice !== null ? displayPrice - cheapestDisplayPrice : 0;
                    return (
                      <tr key={index} className={`border-b border-gray-50 ${isCheapest ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                        <td className="px-3 py-3 text-gray-700 flex items-center gap-2">
                          {storePrice.store_logo && <img src={storePrice.store_logo} alt="" className="h-6 w-6 object-contain rounded-sm flex-shrink-0"/>}
                          <span className="font-medium">{storePrice.store_name}</span>
                          {isCheapest && (
                            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap">Best price</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className={`font-bold ${isCheapest ? 'text-green-700' : 'text-gray-800'}`}>
                            ${displayPrice.toFixed(hasUnitPricing && storePrice.unit_price ? 3 : 2)}
                            {hasUnitPricing && storePrice.unit && (
                              <span className="text-xs font-normal text-gray-400">/{storePrice.unit}</span>
                            )}
                          </span>
                          {!isCheapest && diff > 0 && (
                            <span className="block text-[10px] text-gray-400">+${diff.toFixed(hasUnitPricing ? 3 : 2)} more</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-gray-400 text-xs hidden sm:table-cell">
                          {storePrice.last_updated ? new Date(storePrice.last_updated).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Price history — secondary, below comparison */}
      {product.price_history_data && product.price_history_data.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-5 sm:p-6">
          <h2 className="text-lg font-semibold mb-2 text-gray-800">Price History</h2>
          <p className="text-xs text-gray-400 mb-2">Recent price changes for this product</p>
          <BasicPriceChart historyData={product.price_history_data} />
        </div>
      )}
    </div>
  );
}
